import React, { useState } from "react";
import { UserPlus, Send, CheckCircle2, Loader2, HeartHandshake, GraduationCap, Users } from "lucide-react";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const empty = {
  name: "", email: "", phone: "", qualification: "",
  experience: "", specialization: "", city: "", linkedin: "", photo: "", message: "",
};

const perks = [
  { icon: Users, title: "Reach More Students", text: "Connect with students across schools and colleges seeking guidance." },
  { icon: GraduationCap, title: "Flexible Involvement", text: "Choose assignments that fit your expertise and availability." },
  { icon: HeartHandshake, title: "Make an Impact", text: "Shape confident career decisions and change young lives." },
];

export default function CounsellorRegister() {
  const { toast } = useToast();
  const [form, setForm] = useState(empty);
  const [state, setState] = useState("idle");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.post("/counsellors", form);
      setState("sent");
      toast({ title: "Application received!", description: "Thank you for joining. Our team will review and reach out." });
      setForm(empty);
      setTimeout(() => setState("idle"), 4500);
    } catch (err) {
      setState("idle");
      toast({ title: "Something went wrong", description: "Please try again shortly.", variant: "destructive" });
    }
  };

  return (
    <section id="join" className="bg-[#f6f8fc] py-16 md:py-24">
      <div className="max-w-[1240px] mx-auto px-5">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#2b8cf0]/10 text-[#2b8cf0] text-xs font-semibold font-head">
            <UserPlus size={14} /> JOIN OUR NETWORK
          </span>
          <h2 className="font-head text-3xl md:text-4xl font-bold text-[#16244F] mt-4">
            Become a Career Counsellor
          </h2>
          <div className="mx-auto mt-4 h-1 w-20 rounded-full bg-[#F58220]" />
          <p className="max-w-2xl mx-auto mt-6 text-[#5a6274]">
            Are you passionate about guiding students toward the right path? Register to join our growing network of
            expert counsellors and help shape the next generation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-10 items-start">
          <div className="space-y-5">
            {perks.map((p) => (
              <div key={p.title} className="flex gap-4 bg-white rounded-2xl p-5 cc-shadow-card">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-[#eaf3ff] text-[#2b8cf0] flex items-center justify-center">
                  <p.icon size={24} />
                </div>
                <div>
                  <h3 className="font-head font-semibold text-[#16244F]">{p.title}</h3>
                  <p className="text-sm text-[#5a6274] mt-1">{p.text}</p>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="bg-white rounded-2xl p-7 md:p-9 cc-shadow-card">
            <h3 className="font-head text-xl font-bold text-[#16244F] mb-5">Counsellor Registration</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input name="name" value={form.name} onChange={onChange} required placeholder="Full name *"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
              <input name="email" type="email" value={form.email} onChange={onChange} required placeholder="Email *"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
              <input name="phone" value={form.phone} onChange={onChange} required placeholder="Phone *"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
              <input name="city" value={form.city} onChange={onChange} placeholder="City"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
              <input name="qualification" value={form.qualification} onChange={onChange} required placeholder="Highest qualification *"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
              <input name="experience" value={form.experience} onChange={onChange} placeholder="Years of experience"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
              <input name="specialization" value={form.specialization} onChange={onChange} placeholder="Specialization (e.g. STEM, Abroad)"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
              <input name="linkedin" value={form.linkedin} onChange={onChange} placeholder="LinkedIn / Portfolio URL"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
              <input name="photo" value={form.photo} onChange={onChange} placeholder="Photo URL (optional)"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
            </div>
            <textarea name="message" value={form.message} onChange={onChange} rows={3} placeholder="Tell us why you'd like to join..."
              className="w-full mt-4 px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition resize-none" />
            <button type="submit" disabled={state !== "idle"}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold py-3.5 rounded-lg transition-colors disabled:opacity-80">
              {state === "sending" ? (<><Loader2 size={18} className="animate-spin" /> Submitting...</>)
                : state === "sent" ? (<><CheckCircle2 size={18} /> Application Sent</>)
                : (<><Send size={18} /> Submit Application</>)}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
